import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { del, get, put, errorMessage } from '@/shared/api/client';
import { Badge, Button, Card, Field, Input, Skeleton, Table } from '@/shared/ui';
import { formatClp } from '@/shared/lib/format';
import { useToast } from '@/app/providers';

type User = { id: number; email: string; full_name: string; is_active: boolean };
type Limit = { user_id: number; limit_clp: number; source: 'GLOBAL' | 'USUARIO' };

/** Administración → Límites FXR: límite individual (sobrescribe el global §6.1) con vigencias versionadas. */
export function LimitsPage() {
  const qc = useQueryClient();
  const toast = useToast();
  const [q, setQ] = useState('');
  const [sel, setSel] = useState<User | null>(null);
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const users = useQuery({ queryKey: ['admin', 'users', q], queryFn: () => get<{ items: User[] }>(`/admin/users?page_size=50${q ? `&q=${encodeURIComponent(q)}` : ''}`) });
  const limit = useQuery({ queryKey: ['admin', 'limit', sel?.id], queryFn: () => get<Limit>(`/admin/limits/${sel!.id}`), enabled: !!sel });
  const ok = () => { qc.invalidateQueries({ queryKey: ['admin', 'limit'] }); toast.notify({ title: 'Límite actualizado' }); setAmount(''); setReason(''); };
  const setL = useMutation({ mutationFn: () => put(`/admin/limits/${sel!.id}`, { limit_clp: Number.parseInt(amount, 10), reason }), onSuccess: ok, onError: (e) => toast.notify({ title: 'Error', description: errorMessage(e), kind: 'error' }) });
  const clear = useMutation({ mutationFn: () => del(`/admin/limits/${sel!.id}`, { reason: reason || 'vuelve al límite global' }), onSuccess: ok, onError: (e) => toast.notify({ title: 'Error', description: errorMessage(e), kind: 'error' }) });
  return (
    <>
      <h1 style={{ marginBottom: 'var(--sp-5)' }}>Límites FXR por usuario</h1>
      <div style={{ display: 'grid', gap: 'var(--sp-4)', gridTemplateColumns: 'minmax(280px, 1fr) minmax(280px, 1fr)' }}>
        <Card title="Usuarios">
          <Input placeholder="Buscar" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Buscar usuarios" />
          <div style={{ marginTop: 'var(--sp-3)' }}>
            {users.isPending && <Skeleton height={60} />}
            {users.data && (
              <Table>
                <tbody>{users.data.items.map((u) => <tr key={u.id} onClick={() => setSel(u)} style={{ cursor: 'pointer', background: sel?.id === u.id ? 'var(--sgr-bg-muted)' : undefined }}><td>{u.full_name}<div style={{ fontSize: 'var(--fs-xs)', color: 'var(--sgr-text-muted)' }}>{u.email}</div></td><td>{!u.is_active && <Badge tone="rejected">Inactivo</Badge>}</td></tr>)}</tbody>
              </Table>
            )}
          </div>
        </Card>
        <Card title={sel ? `Límite de ${sel.full_name}` : 'Selecciona un usuario'}>
          {sel && limit.data && (
            <>
              <p>Vigente: <strong className="num">{formatClp(limit.data.limit_clp)}</strong> <Badge tone={limit.data.source === 'USUARIO' ? 'review' : 'neutral'}>{limit.data.source === 'USUARIO' ? 'individual' : 'global'}</Badge></p>
              <Field label="Nuevo límite CLP" htmlFor="lim"><Input id="lim" inputMode="numeric" value={amount} onChange={(e) => setAmount(e.target.value.replace(/[^0-9]/g, ''))} /></Field>
              <Field label="Motivo" htmlFor="lim-reason"><Input id="lim-reason" value={reason} onChange={(e) => setReason(e.target.value)} /></Field>
              <div style={{ display: 'flex', gap: 'var(--sp-2)' }}>
                <Button onClick={() => setL.mutate()} loading={setL.isPending} disabled={!amount || reason.length < 3}>Fijar límite individual</Button>
                {limit.data.source === 'USUARIO' && <Button variant="secondary" onClick={() => clear.mutate()} loading={clear.isPending}>Volver al global</Button>}
              </div>
            </>
          )}
        </Card>
      </div>
    </>
  );
}
